import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import gogiDarkTheme from "./theme";
import Dashboard from "./pages/Dashboard.tsx";
import LiveTrading from "./pages/LiveTrading.tsx";
import Algos from "./pages/Algos.tsx";
import AlgoStudio from "./pages/AlgoStudio.tsx";
import InstanceDetails from "./pages/InstanceDetails.tsx";
import ManageInstance from "./pages/ManageInstance.tsx";
import InstanceNotifications from "./pages/InstanceNotifications.tsx";
import AlgoCode from "./pages/AlgoCode.tsx";
import Header from "./components/Header.tsx";
import Login from "./pages/Login.tsx";
import "bootstrap/dist/css/bootstrap.min.css";
import Backtest from "./pages/Backtest.tsx";
import Settings from "./pages/Settings.tsx";
import ApiKeys from "./pages/ApiKeys.tsx";
import LinkedAlgoEdit from "./pages/LinkedAlgoEdit";
import CheckoutDemo from "./pages/CheckoutDemo.tsx";
import TestTrade from "./pages/TestTrade.tsx";
import TestTradeManagement from "./pages/TestTradeManagement.tsx";
import { clearToken, getToken, verifySession } from "./services/auth";

function AppShell() {
  return (
    <ThemeProvider theme={gogiDarkTheme}>
      <CssBaseline />
      <DndProvider backend={HTML5Backend}>
        <Router>
          <div className="gogi-app">
            <Header />
            <div className="container-fluid gogi-main">
              <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/algos" element={<Algos />} />
              <Route path="/algo-studio" element={<AlgoStudio />} />
              <Route path="/algo-studio/:id" element={<AlgoStudio />} />
              <Route path="/backtest" element={<Backtest />} />
              <Route path="/live-trading" element={<LiveTrading />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/instances/:id" element={<InstanceDetails />} />
              <Route
                path="/instance-details/:id"
                element={<InstanceDetails />}
              />
              <Route
                path="/instance-notifications/:id"
                element={<InstanceNotifications />}
              />
              <Route path="/algo-code/:id" element={<AlgoCode />} />
              <Route path="/manage-instance/:id" element={<ManageInstance />} />
              <Route
                path="/instances/:instanceId/linked-algos/:linkedAlgoId/edit"
                element={<LinkedAlgoEdit />}
              />
              <Route path="/checkout-demo" element={<CheckoutDemo />} />
              <Route path="/test-trade" element={<TestTrade />} />
              <Route path="/test-trade-management" element={<TestTradeManagement />} />
              </Routes>
            </div>
          </div>
        </Router>
      </DndProvider>
    </ThemeProvider>
  );
}

const App: React.FC = () => {
  const [authState, setAuthState] = useState<"checking" | "authenticated" | "unauthenticated">(
    "checking"
  );

  const checkAuth = async () => {
    if (!getToken()) {
      setAuthState("unauthenticated");
      return;
    }
    try {
      const ok = await verifySession();
      setAuthState(ok ? "authenticated" : "unauthenticated");
    } catch {
      clearToken();
      setAuthState("unauthenticated");
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  if (authState === "checking") {
    return (
      <ThemeProvider theme={gogiDarkTheme}>
        <CssBaseline />
        <div className="gogi-app min-vh-100 d-flex flex-column align-items-center justify-content-center text-muted">
          <ActivitySpinner />
          <p className="mt-3 mb-0">Loading…</p>
        </div>
      </ThemeProvider>
    );
  }

  if (authState === "unauthenticated") {
    return <Login onSuccess={checkAuth} />;
  }

  return <AppShell />;
};

function ActivitySpinner() {
  return (
    <div
      className="spinner-border text-primary"
      role="status"
      style={{ width: "2rem", height: "2rem" }}
    >
      <span className="visually-hidden">Loading…</span>
    </div>
  );
}

export default App;
